import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtEur, fmtPct, fmtDate, EVENT_STATUS, COST_CATEGORIES, labelOf, VAT_RATES, vatOf, CASSE, cassaOf } from "@/lib/format";
import { Plus, Trash2, Pencil, X, CalendarDays, TrendingUp, TrendingDown, Loader2, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { PAY_STATUS, paymentInfo, PaymentStrip, PaymentsModal } from "@/components/EventPayments";
import { MonthGroups } from "@/components/MonthGroups";

const emptyEvent = () => ({ name: "", client: "", type: "matrimonio", date: "", location: "", revenue: 0, revenue_vat_rate: 22, pagamento_contanti: false, cassa: "conto", costs: [], status: "confermato", notes: "" });

const STATUS_STYLE = {
  preventivo: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  confermato: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  completato: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  annullato: "bg-slate-500/10 text-slate-400 border-slate-500/20",
};

const totalCosts = (e) => (e.costs || []).reduce((s, c) => s + Number(c.amount || 0), 0);

export default function Events() {
  const [events, setEvents] = useState([]);
  const [reportByEvent, setReportByEvent] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyEvent());
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [typeModal, setTypeModal] = useState(false);
  const [newType, setNewType] = useState("");

  const [typeFilter, setTypeFilter] = useState("tutti");
  const [statusFilter, setStatusFilter] = useState("tutti");
  const [payFilter, setPayFilter] = useState("tutti");
  const [payEvent, setPayEvent] = useState(null);
  const onPaymentsChange = (upd) => { setEvents((prev) => prev.map((x) => (x.id === upd.id ? upd : x))); setPayEvent(upd); };

  const load = () => Promise.all([api.listEvents(), api.listReports(), api.listEventTypes()])
    .then(([d, reps, types]) => { setEvents(d); setReportByEvent(Object.fromEntries((reps || []).map((r) => [r.event_id, r.totals]))); setEventTypes(types); setLoading(false); });
  useEffect(() => { load(); }, []);

  const addType = async () => {
    if (!newType.trim()) return toast.error("Inserisci il nome del tipo");
    try { await api.createEventType({ label: newType.trim() }); setNewType(""); toast.success("Tipo aggiunto"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const delType = async (id) => { await api.deleteEventType(id); toast.success("Tipo eliminato"); load(); };

  const openNew = () => { setForm(emptyEvent()); setEditId(null); setModal(true); };
  const openEdit = (e) => { setForm({ ...emptyEvent(), ...e }); setEditId(e.id); setModal(true); };

  const addCost = () => setForm((f) => ({ ...f, costs: [...f.costs, { label: "", category: "tecnici", amount: 0, vat_rate: 22 }] }));
  const updCost = (i, key, val) => setForm((f) => { const c = [...f.costs]; c[i] = { ...c[i], [key]: val }; return { ...f, costs: c }; });
  const rmCost = (i) => setForm((f) => ({ ...f, costs: f.costs.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome dell'evento");
    const payload = {
      ...form,
      revenue: Number(form.revenue) || 0,
      revenue_vat_rate: form.pagamento_contanti ? 0 : (Number(form.revenue_vat_rate) || 0),
      pagamento_contanti: !!form.pagamento_contanti,
      costs: form.costs.map((c) => ({ label: c.label, category: c.category, amount: Number(c.amount) || 0, vat_rate: Number(c.vat_rate) || 0 })),
    };
    try {
      if (editId) { await api.updateEvent(editId, payload); toast.success("Evento aggiornato"); }
      else { await api.createEvent(payload); toast.success("Evento creato"); }
      setModal(false); load();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const del = async (id) => { await api.deleteEvent(id); toast.success("Evento eliminato"); load(); };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>;

  const q = search.trim().toLowerCase();
  const chip = (active) => `px-3 py-1.5 rounded-lg text-sm transition-colors ${active ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "bg-[#1A2335] text-slate-400 border border-[#222D42]"}`;
  const filtered = events.filter((e) => {
    if (typeFilter !== "tutti" && e.type !== typeFilter) return false;
    if (statusFilter !== "tutti" && e.status !== statusFilter) return false;
    if (payFilter === "da_incassare" && paymentInfo(e).rest <= 0) return false;
    if (!(["tutti", "da_incassare"].includes(payFilter)) && paymentInfo(e).status !== payFilter) return false;
    if (q && ![e.name, e.client, e.location, e.date, fmtDate(e.date), labelOf(eventTypes, e.type)]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))) return false;
    return true;
  });
  const sorted = [...filtered].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight text-slate-100">Eventi</h1>
          <p className="text-slate-500 text-sm mt-1">Guadagno o perdita per singolo evento</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="manage-event-types-button" onClick={() => setTypeModal(true)} className="bg-[#1A2335] hover:bg-[#222D42] text-slate-200 border border-[#222D42] font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2">
            <Settings2 className="w-4 h-4" /> Gestisci Tipi
          </button>
          <button data-testid="add-event-button" onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2 active:scale-[0.98]">
            <Plus className="w-4 h-4" /> Nuovo Evento
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="event-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, cliente, luogo o data…"
          className="w-full bg-[#121824] border border-[#222D42] rounded-lg pl-10 pr-10 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50"
        />
        {search && (
          <button data-testid="clear-search-button" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 mr-1">Tipo:</span>
        <button data-testid="type-filter-tutti" onClick={() => setTypeFilter("tutti")} className={chip(typeFilter === "tutti")}>Tutti</button>
        {eventTypes.map((t) => <button key={t.value} data-testid={`type-filter-${t.value}`} onClick={() => setTypeFilter(t.value)} className={chip(typeFilter === t.value)}>{t.label}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 mr-1">Stato:</span>
        <button data-testid="status-filter-tutti" onClick={() => setStatusFilter("tutti")} className={chip(statusFilter === "tutti")}>Tutti</button>
        {EVENT_STATUS.map((t) => <button key={t.value} data-testid={`status-filter-${t.value}`} onClick={() => setStatusFilter(t.value)} className={chip(statusFilter === t.value)}>{t.label}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 mr-1">Pagamento:</span>
        <button data-testid="pay-filter-tutti" onClick={() => setPayFilter("tutti")} className={chip(payFilter === "tutti")}>Tutti</button>
        {PAY_STATUS.map((t) => <button key={t.value} data-testid={`pay-filter-${t.value}`} onClick={() => setPayFilter(t.value)} className={chip(payFilter === t.value)}>{t.label}</button>)}
        <button data-testid="pay-filter-da_incassare" onClick={() => setPayFilter("da_incassare")} className={chip(payFilter === "da_incassare")}>Da incassare</button>
      </div>

      {events.length === 0 && (
        <div className="bg-[#121824] border border-[#222D42] rounded-xl p-10 text-center">
          <CalendarDays className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessun evento ancora. Aggiungi il primo per calcolare il margine.</p>
        </div>
      )}

      {events.length > 0 && filtered.length === 0 && (
        <div className="bg-[#121824] border border-[#222D42] rounded-xl p-10 text-center">
          <Search className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessun evento trovato con i filtri attuali.</p>
        </div>
      )}

      <div>
        <MonthGroups items={sorted} dateOf={(e) => e.date} forceOpen={!!q} testPrefix="events" render={(e) => {
          const costs = totalCosts(e);
          const vatCosts = (e.costs || []).reduce((s, c) => s + vatOf(c.amount, c.vat_rate), 0);
          const vatRev = e.pagamento_contanti ? 0 : vatOf(e.revenue, e.revenue_vat_rate);
          const margin = Number(e.revenue || 0) - costs;
          const pct = e.revenue ? (margin / e.revenue) * 100 : 0;
          const rep = reportByEvent[e.id];
          const realMargin = rep ? Number(rep.margin || 0) : null;
          const realPct = rep && rep.revenue ? (realMargin / rep.revenue) * 100 : null;
          return (
            <div key={e.id} data-testid={`event-card-${e.id}`} className="bg-[#121824] border border-[#222D42] rounded-xl p-5 hover:border-[#384B6E] transition-colors group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-head font-semibold text-slate-100 truncate">{e.name}</h3>
                  <p className="text-slate-500 text-xs mt-0.5">{e.client || "—"}</p>
                  {e.pagamento_contanti && <span data-testid={`event-contanti-badge-${e.id}`} className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-slate-500/30 bg-slate-500/10 text-slate-300">contanti · no fattura</span>}
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLE[e.status] || STATUS_STYLE.confermato}`}>{labelOf(EVENT_STATUS, e.status)}</span>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                <span>{labelOf(eventTypes, e.type)}</span>
                <span>·</span>
                <span>{fmtDate(e.date)}</span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#1A2335] rounded-lg py-2">
                  <p className="text-[10px] uppercase text-slate-500">Ricavo</p>
                  <p className="font-mono text-sm text-slate-200">{fmtEur(e.revenue)}</p>
                </div>
                <div className="bg-[#1A2335] rounded-lg py-2">
                  <p className="text-[10px] uppercase text-slate-500">Costi</p>
                  <p className="font-mono text-sm text-slate-400">{fmtEur(costs)}</p>
                </div>
                <div className={`rounded-lg py-2 ${margin >= 0 ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                  <p className="text-[10px] uppercase text-slate-500">Margine</p>
                  <p className={`font-mono text-sm font-semibold ${margin >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtEur(margin)}</p>
                </div>
              </div>

              {(vatCosts > 0 || vatRev > 0) && (
                <div className="mt-2 space-y-1">
                  {vatRev > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>IVA sul ricavo (a debito)</span>
                      <span className="font-mono text-sky-300">{fmtEur(vatRev)}</span>
                    </div>
                  )}
                  {vatCosts > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>IVA sui costi (a credito)</span>
                      <span className="font-mono text-amber-300">{fmtEur(vatCosts)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Saldo IVA evento</span>
                    <span className={`font-mono font-semibold ${vatRev - vatCosts >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtEur(vatRev - vatCosts)}</span>
                  </div>
                </div>
              )}

              <div className="mt-3 grid grid-cols-2 gap-2">
                <div data-testid={`event-pct-stimata-${e.id}`} className="rounded-lg py-2 px-3 bg-sky-500/10 border border-sky-500/20">
                  <p className="text-[10px] uppercase text-sky-400/70 tracking-wider">% Stimata</p>
                  <p className={`font-mono text-sm font-semibold inline-flex items-center gap-1 ${pct >= 0 ? "text-sky-300" : "text-red-400"}`}>
                    {pct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />} {fmtPct(pct)}
                  </p>
                </div>
                <div data-testid={`event-pct-reale-${e.id}`} className={`rounded-lg py-2 px-3 ${rep ? "bg-amber-500/10 border border-amber-500/20" : "bg-[#1A2335] border border-dashed border-[#2B3850]"}`}>
                  <p className={`text-[10px] uppercase tracking-wider ${rep ? "text-amber-400/70" : "text-slate-600"}`}>% Reale</p>
                  {rep ? (
                    <p className={`font-mono text-sm font-semibold inline-flex items-center gap-1 ${realPct >= 0 ? "text-amber-300" : "text-red-400"}`}>
                      {realPct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />} {fmtPct(realPct)}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-600 mt-0.5">nessun report</p>
                  )}
                </div>
              </div>

              {rep && (
                <div className="mt-2 flex items-center justify-between text-xs bg-[#1A2335] rounded-lg px-3 py-1.5 border border-[#222D42]">
                  <span className="text-slate-400">Margine reale</span>
                  <span className="font-mono"><span className={`font-semibold ${realMargin >= 0 ? "text-amber-300" : "text-red-400"}`}>{fmtEur(realMargin)}</span> <span className="text-slate-600">· stim. {fmtEur(margin)}</span></span>
                </div>
              )}

              {e.status !== "annullato" && Number(e.revenue || 0) > 0 && <PaymentStrip event={e} onOpen={setPayEvent} />}

              <div className="mt-3 flex items-center justify-end">
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-event-${e.id}`} onClick={() => openEdit(e)} className="p-1.5 rounded-md hover:bg-[#222D42] text-slate-400"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-event-${e.id}`} onClick={() => del(e.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        }} />
      </div>

      <PaymentsModal event={payEvent} onClose={() => setPayEvent(null)} onChange={onPaymentsChange} />

      {filtered.length > 0 && <p className="text-xs text-slate-500 text-center" data-testid="events-count">{filtered.length} eventi in totale</p>}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModal(false)} />
          <div className="relative bg-[#121824] border border-[#222D42] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#222D42] sticky top-0 bg-[#121824] z-10">
              <h3 className="font-head text-lg font-semibold text-slate-100">{editId ? "Modifica Evento" : "Nuovo Evento"}</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Nome evento">
                <input data-testid="event-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" placeholder="Es. Matrimonio Villa Rosa" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Cliente"><input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className="inp" /></Field>
                <Field label="Data"><input type="date" data-testid="event-date-input" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="inp" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tipo"><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="inp">{eventTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></Field>
                <Field label="Stato"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="inp">{EVENT_STATUS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Luogo"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="inp" /></Field>
                <Field label="Ricavo netto (€)"><input type="number" data-testid="event-revenue-input" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} className="inp font-mono" /></Field>
              </div>
              <div><label className="lbl">Cassa / incasso</label><select data-testid="event-cassa-select" value={form.cassa || cassaOf(form)} onChange={(e) => setForm({ ...form, cassa: e.target.value, pagamento_contanti: e.target.value === "nero" })} className="inp">{CASSE.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select><p className="text-[11px] text-slate-500 mt-1">{(form.cassa || cassaOf(form)) === "nero" ? "In nero: non contabilizzato, no IVA." : "Fatturato: contabilizzato e con IVA a debito."}</p></div>
              {!form.pagamento_contanti && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="IVA sul ricavo"><select data-testid="event-revenue-vat-select" value={form.revenue_vat_rate} onChange={(e) => setForm({ ...form, revenue_vat_rate: Number(e.target.value) })} className="inp">{VAT_RATES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></Field>
                  <Field label="Ricavo IVA inclusa"><div className="inp font-mono text-emerald-400">{fmtEur(Number(form.revenue || 0) + vatOf(form.revenue, form.revenue_vat_rate))}</div></Field>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Costi</label>
                  <button data-testid="add-cost-button" onClick={addCost} className="text-amber-400 text-xs flex items-center gap-1 hover:text-amber-300"><Plus className="w-3.5 h-3.5" /> Aggiungi costo</button>
                </div>
                <div className="space-y-2">
                  {form.costs.map((c, i) => (
                    <div key={i} className="flex flex-wrap gap-2 items-center">
                      <input value={c.label} onChange={(e) => updCost(i, "label", e.target.value)} placeholder="Voce" className="inp flex-1 min-w-[120px]" />
                      <select value={c.category} onChange={(e) => updCost(i, "category", e.target.value)} className="inp w-28">{COST_CATEGORIES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select>
                      <input type="number" value={c.amount} onChange={(e) => updCost(i, "amount", e.target.value)} className="inp w-20 font-mono" placeholder="netto" />
                      <select value={c.vat_rate ?? 0} onChange={(e) => updCost(i, "vat_rate", Number(e.target.value))} className="inp w-[4.5rem]" title="Aliquota IVA">{VAT_RATES.map((t) => <option key={t.value} value={t.value}>{t.value}%</option>)}</select>
                      <button onClick={() => rmCost(i)} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {form.costs.length === 0 && <p className="text-slate-600 text-xs">Nessun costo. Aggiungi tecnici, furgone, consumabili, ammortamento…</p>}
                </div>
                <div className="text-right text-sm font-mono text-slate-400 mt-2 space-y-0.5">
                  <p>Imponibile: {fmtEur(form.costs.reduce((s, c) => s + Number(c.amount || 0), 0))}</p>
                  <p>IVA: {fmtEur(form.costs.reduce((s, c) => s + vatOf(c.amount, c.vat_rate), 0))}</p>
                  <p className="text-amber-400 font-semibold">Totale IVA incl.: {fmtEur(form.costs.reduce((s, c) => s + Number(c.amount || 0) + vatOf(c.amount, c.vat_rate), 0))}</p>
                </div>
              </div>

              <Field label="Note"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="inp min-h-[60px]" /></Field>
            </div>
            <div className="p-5 border-t border-[#222D42] flex justify-end gap-2 sticky bottom-0 bg-[#121824]">
              <button onClick={() => setModal(false)} className="bg-[#1A2335] hover:bg-[#222D42] text-slate-200 border border-[#222D42] font-medium px-4 py-2 rounded-lg transition-colors">Annulla</button>
              <button data-testid="save-event-button" onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-lg transition-colors">Salva</button>
            </div>
          </div>
        </div>
      )}
      {typeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setTypeModal(false)} />
          <div className="relative bg-[#121824] border border-[#222D42] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#222D42]">
              <h3 className="font-head text-lg font-semibold text-slate-100">Tipologie di evento</h3>
              <button onClick={() => setTypeModal(false)} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-500">Gestisci le tipologie di evento (es. Matrimonio, Concerto, Battesimo). Sono subito disponibili nel form di creazione evento.</p>
              <div className="flex gap-2">
                <input data-testid="new-event-type-input" value={newType} onChange={(e) => setNewType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addType()} placeholder="Nome nuovo tipo" className="inp flex-1" />
                <button data-testid="save-event-type-button" onClick={addType} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1 shrink-0"><Plus className="w-4 h-4" /> Aggiungi</button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {eventTypes.length === 0 && <p className="text-slate-600 text-sm">Nessun tipo.</p>}
                {eventTypes.map((t) => (
                  <div key={t.id} data-testid={`event-type-row-${t.value}`} className="flex items-center justify-between bg-[#1A2335] border border-[#222D42] rounded-lg px-3 py-2">
                    <span className="flex items-center gap-2 text-sm text-slate-200"><CalendarDays className="w-3.5 h-3.5 text-slate-500" />{t.label}</span>
                    <button data-testid={`delete-event-type-${t.value}`} onClick={() => delType(t.id)} className="text-red-400 p-1 rounded hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 border-t border-[#222D42] flex justify-end">
              <button onClick={() => setTypeModal(false)} className="bg-[#1A2335] hover:bg-[#222D42] text-slate-200 border border-[#222D42] font-medium px-4 py-2 rounded-lg transition-colors">Chiudi</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;background:#1A2335;border:1px solid #222D42;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#F1F5F9}.inp:focus{outline:none;border-color:rgba(229,169,60,.5)}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium block mb-1.5">{label}</label>
    {children}
  </div>
);
